'use client';

import { useEffect, useState } from 'react';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { NextAppointmentsSectionCard } from './next-appointments-section-card.component';

import { ContentContainer } from '@/components/ui/content-container.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getNextAppointments, NextAppointment } from '@/services/appointment/get-next-appointments.service';
import { authStore } from '@/store/auth.store';
import { prioritizeUnscheduledFlexAppointments } from '@/utils/appointment/prioritize-unscheduled-flex-appointments.util';

const MAX_APPOINTMENTS = 3;

export function NextAppointmentsSection() {
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const [appointments, setAppointments] = useState<NextAppointment[]>();

  useEffect(() => {
    if (!isLoggedIn) {
      setAppointments(undefined);
      return;
    }

    let cancelled = false;

    getNextAppointments()
      .then((response) => {
        if (!cancelled) {
          setAppointments(response);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppointments([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  if (!isLoggedIn || appointments === undefined) {
    return null;
  }

  const sortedAppointments = prioritizeUnscheduledFlexAppointments(appointments);
  const visibleAppointments = sortedAppointments.slice(0, MAX_APPOINTMENTS);

  if (visibleAppointments.length === 0) {
    return null;
  }

  return (
    <section className="mt-6">
      <ContentContainer>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <Typography
              as="h2"
              variant={TypographyVariant.BODY_TEXT}
              className="font-semibold text-gray-700"
              data-testid={DataTestIdEnum.HOME_NEXT_APPOINTMENTS_SECTION}
            >
              Próximos atendimentos
            </Typography>
            <Link
              prefetch={false}
              href="/agendamentos"
              data-testid={DataTestIdEnum.HOME_NEXT_APPOINTMENTS_SEE_ALL}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-green-500 hover:underline"
            >
              <Icon icon={faArrowRight} size="sm" />
              Ver todos
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar xl:grid xl:grid-cols-3 xl:overflow-visible">
            {visibleAppointments.map((appointment) => (
              <NextAppointmentsSectionCard
                key={appointment.id}
                appointment={appointment}
              />
            ))}
          </div>
        </div>
      </ContentContainer>
    </section>
  );
}
